import React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Row, RightComponents, LeftComponents } from '../../components/Layout';
import { Button, MultiSelectBox, SearchBox } from '../../components/Form';
import { TableData, TableHeader, TableBody, TableRow, TableColumn } from '../../components/Table';
import { MobileInfoBox, Title, TextView, PanelBox, Modal } from '../../components/Display';
import { postData } from '../../Api';
import TaroffTaskView from './TaroffTaskView';
import TaroffTaskForm from './TaroffTaskForm';
import TaroffBaleConnect from './TaroffBaleConnect';

const PRIORITY_PILL = {
    1: { label: 'بالا', className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800', icon: 'fas fa-arrow-up' },
    2: { label: 'عادی', className: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-zinc-800 dark:text-gray-300 dark:border-zinc-700', icon: 'fas fa-minus' },
    3: { label: 'پایین', className: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800', icon: 'fas fa-arrow-down' },
};

export default function TaroffTasks(props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [modal, setModal] = useState(null);
    const allProps = { ...props, ...Object.fromEntries(searchParams.entries()) };

    const [formQAssigneeIdOptions, setFormQAssigneeIdOptions] = useState([]);
    const [formQCreatorIdOptions, setFormQCreatorIdOptions] = useState([]);
    const [formQStateIdOptions, setFormQStateIdOptions] = useState([]);
    const [formQTypeIdOptions, setFormQTypeIdOptions] = useState([]);
    useEffect(() => {
        postData('/api/panel/page/taroff-tasks/select-data/q-assignee-id').then((r) => setFormQAssigneeIdOptions(r.data || []));
        postData('/api/panel/page/taroff-tasks/select-data/q-creator-id').then((r) => setFormQCreatorIdOptions(r.data || []));
        postData('/api/panel/page/taroff-tasks/select-data/q-state-id').then((r) => setFormQStateIdOptions(r.data || []));
        postData('/api/panel/page/taroff-tasks/select-data/q-type-id').then((r) => setFormQTypeIdOptions(r.data || []));
    }, []);

    const [summary, setSummary] = useState({});
    useEffect(() => {
        postData('/api/panel/page/taroff-tasks/summary').then((response) => setSummary(response.data || {}));
    }, [searchParams, modal]);

    const filterQBucket = allProps['q-bucket'] || '';
    const setBucket = (bucket) => {
        const params = { ...Object.fromEntries(searchParams.entries()) };
        if (bucket) params['q-bucket'] = bucket; else delete params['q-bucket'];
        delete params['page'];
        navigate({ search: '?' + new URLSearchParams(params).toString() });
    };

    const [tableData, setTableData] = useState({});
    const [tableSize, setTableSize] = useState(parseInt(allProps['size']) || 20);
    const [tablePage, setTablePage] = useState(parseInt(allProps['page']) || 1);
    const gotoPage = (page, size = undefined) => {
        setTableData({});
        setTablePage(page);
        size && setTableSize(size);
        setSearchParams({ ...searchParams, page: page, size: size || tableSize });
    };

    const [filterQAssigneeId, setFilterQAssigneeId] = useState(allProps['q-assignee-id'] || '');
    const [filterQCreatorId, setFilterQCreatorId] = useState(allProps['q-creator-id'] || '');
    const [filterQStateId, setFilterQStateId] = useState(allProps['q-state-id'] || '');
    const [filterQTypeId, setFilterQTypeId] = useState(allProps['q-type-id'] || '');
    const [filterQTitle, setFilterQTitle] = useState(allProps['q-title'] || '');

    const clearFilters = () => {
        setFilterQAssigneeId('');
        setFilterQCreatorId('');
        setFilterQStateId('');
        setFilterQTypeId('');
        setFilterQTitle('');
        setSearchParams({});
    };
    const applyFilters = () => {
        const newSearchParams = {};
        filterQBucket && (newSearchParams['q-bucket'] = filterQBucket);
        filterQAssigneeId && (newSearchParams['q-assignee-id'] = filterQAssigneeId);
        filterQCreatorId && (newSearchParams['q-creator-id'] = filterQCreatorId);
        filterQStateId && (newSearchParams['q-state-id'] = filterQStateId);
        filterQTypeId && (newSearchParams['q-type-id'] = filterQTypeId);
        filterQTitle && (newSearchParams['q-title'] = filterQTitle);
        navigate({ search: '?' + new URLSearchParams(newSearchParams).toString() });
    };

    const reload = () => setSearchParams({ ...Object.fromEntries(searchParams.entries()) });

    useEffect(() => {
        postData('/api/panel/page/taroff-tasks/table-data', {
            size: tableSize,
            page: tablePage,
            q_assignee_id: filterQAssigneeId,
            q_creator_id: filterQCreatorId,
            q_state_id: filterQStateId,
            q_type_id: filterQTypeId,
            q_title: filterQTitle,
            q_bucket: filterQBucket,
        }).then((response) => setTableData(response.data));
    }, [searchParams, modal, props]);

    const openTask = (taskId) => {
        setModal(
            Modal({
                children: <TaroffTaskView task-id={taskId} onDismiss={() => setModal(null)} />,
                onDismiss: () => setModal(null),
                width: '820px',
            })
        );
    };

    // A Bale notification links straight to ?open=<id>; honour it once on load.
    useEffect(() => {
        const openId = allProps['open'];
        if (openId) openTask(parseInt(openId));
    }, []);

    const handleCreateTask = () => {
        setModal(
            Modal({
                children: (
                    <TaroffTaskForm
                        onDismiss={() => setModal(null)}
                        onSaved={(id) => { setModal(null); id && openTask(id); }}
                    />
                ),
                onDismiss: () => setModal(null),
                width: '720px',
            })
        );
    };

    const handleBaleConnect = () => {
        setModal(
            Modal({
                children: <TaroffBaleConnect onDismiss={() => setModal(null)} />,
                onDismiss: () => setModal(null),
                width: '560px',
            })
        );
    };

    const handleQuickDone = async (row, e) => {
        e && e.stopPropagation && e.stopPropagation();
        const key = row.flag_open ? 'Done' : 'Opened';
        await postData('/api/panel/page/taroff-task-state-by-key/save', { id: row.text_id, key });
        reload();
    };

    const handleTakeIt = async (row, e) => {
        e && e.stopPropagation && e.stopPropagation();
        await postData('/api/panel/page/taroff-task-assign/save', { id: row.text_id, assignee_id: summary.me_id || null });
        reload();
    };

    return (
        <PanelBox>
            <Row>
                <RightComponents>
                    <Title>کارتابل</Title>
                </RightComponents>
                <LeftComponents>
                    <Button onClick={handleBaleConnect} color={'secondary'} rightIcon={'far fa-paper-plane'}>
                        اطلاع‌رسانی بله
                    </Button>
                    <Button onClick={handleCreateTask} color={'primary'} rightIcon={'far fa-plus'}>
                        کار جدید
                    </Button>
                </LeftComponents>
            </Row>

            <div className="flex flex-wrap gap-2 mb-4">
                {[
                    { key: 'mine', label: 'کارهای من', count: summary.mine },
                    { key: 'awaiting_me', label: 'منتظر من', count: summary.awaiting_me, highlight: true },
                    { key: 'created_by_me', label: 'ثبت‌شده توسط من', count: summary.created_by_me },
                    { key: 'unassigned', label: 'بدون مسئول', count: summary.unassigned },
                    { key: 'open', label: 'باز', count: summary.open },
                    { key: 'closed', label: 'بسته شده', count: summary.closed },
                    { key: '', label: 'همه', count: summary.total },
                ].map((chip) => {
                    const active = filterQBucket === chip.key;
                    const danger = chip.highlight && chip.count > 0;
                    return (
                        <button
                            key={chip.key || 'all'}
                            onClick={() => setBucket(chip.key)}
                            className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
                                active
                                    ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                                    : danger
                                        ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800'
                                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800'
                            }`}
                        >
                            <span>{chip.label}</span>
                            <span className={`min-w-[20px] rounded-full px-1.5 text-xs font-bold ${active ? 'bg-white/20' : danger ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200'}`}>
                                {(chip.count ?? 0).toLocaleString('fa-IR')}
                            </span>
                        </button>
                    );
                })}
            </div>

            <RightComponents wrap={true}>
                <MultiSelectBox label={'مسئول'} options={formQAssigneeIdOptions} value={filterQAssigneeId} onChange={setFilterQAssigneeId} />
                <MultiSelectBox label={'ثبت‌کننده'} options={formQCreatorIdOptions} value={filterQCreatorId} onChange={setFilterQCreatorId} />
                <MultiSelectBox label={'وضعیت'} options={formQStateIdOptions} value={filterQStateId} onChange={setFilterQStateId} />
                <MultiSelectBox label={'نوع'} options={formQTypeIdOptions} value={filterQTypeId} onChange={setFilterQTypeId} />
                <SearchBox onEnter={applyFilters} label={'عنوان'} value={filterQTitle} onChange={setFilterQTitle} width={'120px'} />
                <Button rightIcon={'far fa-search'} onClick={applyFilters}>جستجو</Button>
                <Button rightIcon={'far fa-trash'} onClick={clearFilters}>پاک کردن</Button>
            </RightComponents>

            <TableData total={tableData.total} size={tableSize} page={tablePage} setPage={gotoPage}>
                <TableHeader>
                    <TableRow>
                        <TableColumn mobile={true}>mobile</TableColumn>
                        <TableColumn mobile={false}>شناسه</TableColumn>
                        <TableColumn mobile={false}>عنوان</TableColumn>
                        <TableColumn mobile={false}>مسئول</TableColumn>
                        <TableColumn mobile={false}>ثبت‌کننده</TableColumn>
                        <TableColumn mobile={false}>وضعیت</TableColumn>
                        <TableColumn mobile={false}>نوع</TableColumn>
                        <TableColumn mobile={false}>اولویت</TableColumn>
                        <TableColumn mobile={false}>زمان ایجاد</TableColumn>
                        <TableColumn mobile={false}></TableColumn>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {tableData.rows?.map((row, index) => {
                        const priority = PRIORITY_PILL[row.number_priority] || PRIORITY_PILL[2];
                        return (
                            <TableRow key={index}>
                                <TableColumn mobile={true}>
                                    <MobileInfoBox />
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    <TextView>{row.text_id}</TextView>
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    <TextView bold={true}>{row.text_title}</TextView>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                        {row.flag_awaiting_me && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-xs font-medium dark:bg-red-950 dark:text-red-300 dark:border-red-800">
                                                <i className="far fa-reply"></i>
                                                منتظر شما
                                            </span>
                                        )}
                                        {row.number_comments > 0 && (
                                            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                                <i className="far fa-comments"></i>
                                                {row.number_comments.toLocaleString('fa-IR')}
                                            </span>
                                        )}
                                        {row.text_duedate && (
                                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                                                <i className="far fa-calendar-clock"></i>
                                                <TextView format={'persian-date-normal'}>{row.text_duedate}</TextView>
                                            </span>
                                        )}
                                    </div>
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    {row.text_assignee_title
                                        ? <TextView>{row.text_assignee_title}</TextView>
                                        : <span className="text-xs text-gray-400">تعیین نشده</span>}
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    <TextView>{row.text_creator_title}</TextView>
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    <TextView backColor={row.color_state_id_color}>{row.text_state_id_title}</TextView>
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    <TextView backColor={row.color_type_id_color}>{row.text_type_id_title}</TextView>
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${priority.className}`}>
                                        <i className={`${priority.icon} text-[9px]`} />
                                        {priority.label}
                                    </span>
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    <TextView format={'persian-date-normal'}>{row.text_inserttime}</TextView>
                                </TableColumn>
                                <TableColumn mobile={false}>
                                    <LeftComponents>
                                        <Button onClick={() => openTask(row.text_id)} color={'primary'} rightIcon={'far fa-comments'} tooltip={'مشاهده و گفتگو'}>
                                            گفتگو
                                        </Button>
                                        {!row.text_assignee_title && (
                                            <Button onClick={(e) => handleTakeIt(row, e)} color={'secondary'} rightIcon={'far fa-hand'} tooltip={'برداشتن این کار'}></Button>
                                        )}
                                        <Button
                                            onClick={(e) => handleQuickDone(row, e)}
                                            color={row.flag_open ? 'accent' : 'warn'}
                                            rightIcon={row.flag_open ? 'far fa-check' : 'far fa-rotate-right'}
                                            tooltip={row.flag_open ? 'انجام شد' : 'بازکردن مجدد'}
                                        ></Button>
                                    </LeftComponents>
                                </TableColumn>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </TableData>
            {modal && modal}
        </PanelBox>
    );
}
